<template>
  <el-drawer :visible.sync="show" :with-header="false" size="640px" append-to-body :before-close="beforeClose" @opened="onOpened">
    <div v-if="machine" class="md">
      <!-- 顶部 -->
      <div class="md__top">
        <div class="md__avatar"><i class="el-icon-cpu"></i></div>
        <div class="md__head">
          <div class="md__name">{{ machine.equipmentName || $t("machinesHub.detailTitle") }}</div>
          <div class="md__no vtm-mono">{{ machine.equipmentNo }}</div>
          <div class="md__pills">
            <span v-if="Number(machine.mqState) === 1" class="vtm-pill vtm-pill--ok">{{ $t("machinesHub.online") }}</span>
            <span v-else-if="Number(machine.mqState) === 0" class="vtm-pill vtm-pill--crit">{{ $t("machinesHub.offline") }}</span>
            <span v-else class="vtm-pill vtm-pill--off">{{ $t("machinesHub.unknown") }}</span>
            <span class="vtm-pill" :class="Number(form.enableStatus) === 1 ? 'vtm-pill--ok' : 'vtm-pill--off'">
              {{ Number(form.enableStatus) === 1 ? $t("machinesHub.enabled") : $t("machinesHub.disabled") }}
            </span>
          </div>
          <!-- 续费日期（原列表列迁移至此）+ 费用记录入口（原 Fee Tracker 导航项） -->
          <div v-if="machine.expireDate" class="md__renewal">
            {{ $t("machinesHub.renewalDate") }}:
            <span class="vtm-num" :class="{ 'md__renewal--crit': renewalExpired }">{{ $formatDate(machine.expireDate) }}</span>
            <button v-if="canRenewHistory" type="button" class="md__renewal-link" @click="goRenewHistory">
              {{ $t("machinesHub.renewalHistory") }} <i class="el-icon-arrow-right"></i>
            </button>
          </div>
        </div>
        <button class="md__close" type="button" @click="beforeClose(() => (show = false))"><i class="el-icon-close"></i></button>
      </div>

      <!-- 订阅激活提醒（醒目横幅，跨所有页签可见） -->
      <div v-if="canSubscribe" class="md__subbanner">
        <div class="md__subbanner-text"><i class="el-icon-warning"></i> {{ $t("machinesHub.subscriptionRequired") }}</div>
        <el-button type="primary" size="small" icon="el-icon-s-finance" @click="$emit('open-subscribe', machine)">{{ $t("machinesHub.subscribe") }}</el-button>
      </div>

      <el-tabs v-model="tab" class="md__tabs" :before-leave="beforeTabLeave">
        <!-- 概览（就地编辑 + 自动保存） -->
        <el-tab-pane :label="$t('machinesHub.tabOverview')" name="overview">
          <div class="md__body">
            <div class="md__row">
              <div class="md__field">
                <label class="md__label">{{ $t("machinesHub.name") }}</label>
                <el-input v-model="form.equipmentName" size="small" :class="{ 'md__input--warn': nameIsDefault }"></el-input>
                <div v-if="nameIsDefault" class="md__field-warn">
                  <i class="el-icon-warning-outline"></i> {{ $t("machinesHub.defaultNameWarn") }}
                </div>
              </div>
              <div class="md__field">
                <label class="md__label">{{ $t("machinesHub.group") }}</label>
                <el-select v-model="form.teamId" size="small" filterable clearable :placeholder="$t('machinesHub.noGroup')">
                  <el-option v-for="g in machineGroup" :key="g.id" :label="g.name" :value="g.id"></el-option>
                </el-select>
              </div>
            </div>
            <div class="md__row">
              <div class="md__field">
                <label class="md__label">{{ $t("machinesHub.servicePhone") }}</label>
                <el-input v-model="form.servicePhone" size="small"></el-input>
              </div>
              <div class="md__field md__field--switch">
                <label class="md__label">{{ $t("machinesHub.enabled") }}</label>
                <el-switch v-model="form.enableStatus" :active-value="1" :inactive-value="0"></el-switch>
              </div>
            </div>
            <div class="md__field">
              <label class="md__label">{{ $t("machinesHub.address") }}</label>
              <el-autocomplete
                v-model="form.locale"
                size="small"
                style="width: 100%"
                :fetch-suggestions="queryAddress"
                :placeholder="$t('machinesHub.addAddress')"
                :trigger-on-focus="false"
                popper-class="md-address-suggest"
                @select="onAddressSelect"
              >
                <i slot="prefix" class="el-icon-location-outline" style="line-height: 32px"></i>
                <template slot-scope="{ item }">
                  <div class="md-addr-item">
                    <span class="md-addr-name">{{ item.name }}</span>
                    <span class="md-addr-line">{{ item.address }}</span>
                  </div>
                </template>
              </el-autocomplete>
            </div>
            <div class="md__field">
              <label class="md__label">{{ $t("machinesHub.notes") }}</label>
              <el-input v-model="form.notes" type="textarea" :rows="2" size="small"></el-input>
            </div>

            <!-- 显式保存（不再自动保存）：有改动才可点 -->
            <div class="md__status" :class="'md__status--' + (isDirty ? 'dirty' : saveState)">
              <i v-if="saveState === 'saved' && !isDirty" class="el-icon-circle-check"></i>
              <span>{{ statusText }}</span>
              <el-button size="small" :disabled="!isDirty || saveState === 'saving'" @click="discardChanges">{{ $t("machinesHub.discard") }}</el-button>
              <el-button type="primary" size="small" :disabled="!isDirty" :loading="saveState === 'saving'" @click="doSave">{{ $t("common.save") }}</el-button>
            </div>

            <!-- 快捷操作：未订阅的转移机器不给任何操作入口，订阅才是唯一出口 -->
            <div class="md__actions" v-if="!canSubscribe">
              <el-button type="primary" size="small" icon="el-icon-monitor" @click="tab = 'control'">{{ $t("machinesHub.remoteLogin") }}</el-button>
              <el-button size="small" icon="el-icon-goods" v-hasPerm="['machine:line:product:update']" @click="$emit('open-aisles', machine)">{{ $t("machinesHub.manageAisles") }}</el-button>
            </div>

            <!-- 更多操作（次要，收纳） -->
            <div class="md__more" v-if="!canSubscribe">
              <span class="md__more-label">{{ $t("machinesHub.moreActions") }}</span>
              <div class="md__more-links">
                <el-button type="text" size="mini" @click="$emit('open-inventory', machine)">{{ $t("machinesHub.inventoryLog") }}</el-button>
                <el-button type="text" size="mini" @click="$emit('open-retainer', machine)">{{ $t("machinesHub.takings") }}</el-button>
                <el-button type="text" size="mini" v-hasPerm="['user:equipment:oneconfig']" @click="$emit('open-oneconfig', machine)">{{ $t("machinesHub.oneKeyConfig") }}</el-button>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- 配置（完整第二页签，内嵌，无弹窗） -->
        <el-tab-pane :label="$t('machinesHub.tabConfig')" name="config" v-if="!canSubscribe">
          <div class="md__embed">
            <config-user-machine
              v-if="tab === 'config'"
              ref="cfg"
              :embedded="true"
              :hide-ad-settings="true"
              :dialog-config-machine="true"
              :user-equipment="configSubmit"
              :yes-no="yesNo"
              :age-device="ageDevice"
              :feel-lock="feelLock"
              :age-type="ageType"
              :priority-mode="priorityMode"
              :device-mode="deviceMode"
              :biaoshi="false"
              :idcard-country="idcardCountry"
              @cancel="onSubEditDone"
            />
          </div>
        </el-tab-pane>

        <!-- 支付 -->
        <el-tab-pane :label="$t('machinesHub.tabPayment')" name="payment" v-if="!canSubscribe" v-hasPerm="['user:machine:payment']">
          <div class="md__embed">
            <payment
              v-if="tab === 'payment'"
              :embedded="true"
              :dialog-payment="true"
              :payment="payment"
              :yes-no="yesNo"
              :shua-ka-qi="shuaKaQi"
              :coin-acceptor-item="coinAcceptorItem"
              :bill-acceptor-item="billAcceptorItem"
              @cancelPayment="onSubEditDone"
            />
          </div>
        </el-tab-pane>

        <!-- 控制（远程功能集中） -->
        <el-tab-pane :label="$t('machinesHub.tabControl')" name="control" v-if="!canSubscribe">
          <div class="md__body">
            <machine-control-panel :machine="machine" />
          </div>
        </el-tab-pane>

        <!-- 交易（含营收概要） -->
        <el-tab-pane :label="$t('machinesHub.tabTransactions')" name="transactions" v-if="!canSubscribe">
          <div class="md__body" v-loading="txLoading">
            <div class="md__revenue">
              <div class="md__rev-item">
                <span class="md__rev-val vtm-num">{{ $currency(revenueTotal) }}</span>
                <span class="md__rev-label">{{ $t("machinesHub.revenue") }} · {{ $t("machinesHub.recentWindow") }}</span>
              </div>
              <div class="md__rev-item">
                <span class="md__rev-val vtm-num">{{ txList.length }}</span>
                <span class="md__rev-label">{{ $t("machinesHub.txnCount") }}</span>
              </div>
              <div class="md__rev-item">
                <span class="md__rev-val vtm-num">{{ $currency(avgSale) }}</span>
                <span class="md__rev-label">{{ $t("machinesHub.avgSale") }}</span>
              </div>
            </div>
            <div v-if="!txLoading && txList.length === 0" class="md__empty">{{ $t("machinesHub.noTransactions") }}</div>
            <div v-for="(t, i) in txList" :key="i" class="md__txn">
              <div class="md__txn-main">
                <span class="md__txn-name">{{ t.goodsName || (t.ordersGoods && t.ordersGoods.length ? t.ordersGoods[0].goodsName : "—") }}</span>
                <span class="md__txn-no vtm-mono">{{ t.orderNumber }}</span>
              </div>
              <div class="md__txn-right">
                <span class="md__txn-amt vtm-num">{{ $currency(t.payablePrice) }}</span>
                <span class="md__txn-time vtm-num">{{ $formatDate(t.buyTime) }}</span>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- 广告：本机广告展示开关（迁移自配置页）+ 当前投放一览 + 深链管理 -->
        <el-tab-pane :label="$t('machinesHub.tabAds')" name="ads" v-if="!canSubscribe">
          <div class="md__body" v-loading="adsLoading">
            <!-- 展示设置：控制本机哪些广告位启用 -->
            <div class="md__adcfg">
              <div class="md__adcfg-title">{{ $t("machinesHub.adDisplaySettings") }}</div>
              <div class="md__adcfg-row">
                <div class="md__adcfg-item">
                  <span class="md__adcfg-label">{{ $t("machineConfig.isTAd") }}</span>
                  <el-switch v-model="adCfg.isTAd" :active-value="1" :inactive-value="0"></el-switch>
                </div>
                <div class="md__adcfg-item">
                  <span class="md__adcfg-label">{{ $t("machineConfig.isPAd") }}</span>
                  <el-switch v-model="adCfg.isPAd" :active-value="1" :inactive-value="0"></el-switch>
                </div>
              </div>
              <div v-if="adCfg.isTAd === 1" class="md__adcfg-row">
                <div class="md__adcfg-item">
                  <span class="md__adcfg-label">{{ $t("machinesHub.adRotation") }}</span>
                  <el-switch v-model="adCfg.isCountdown" :active-value="1" :inactive-value="0"></el-switch>
                </div>
                <div v-if="adCfg.isCountdown === 1" class="md__adcfg-item">
                  <span class="md__adcfg-label">{{ $t("machinesHub.adRotationInterval") }}</span>
                  <el-input v-model="adCfg.countdown" size="small" style="width: 90px"><template slot="append">s</template></el-input>
                </div>
              </div>
              <div class="md__adcfg-actions">
                <el-button type="primary" size="small" :loading="adCfgSaving" @click="saveAdSettings">{{ $t("machinesHub.saveAdSettings") }}</el-button>
              </div>
              <!-- 账户级轮播时长（全机通用，只读；到广告枢纽编辑） -->
              <div v-if="acctRotationText" class="md__adcfg-acct">
                <i class="el-icon-time"></i> {{ acctRotationText }}
                <el-button v-if="canManageAds" type="text" size="mini" @click="openAds">{{ $t("machinesHub.editAccountTiming") }}</el-button>
              </div>
            </div>

            <div class="md__ads-head">
              <span v-if="adsList.length" class="md__ads-count">{{ $t("machinesHub.adsRunning", { n: adsList.length }) }}</span>
              <el-button
                v-if="canManageAds"
                type="primary"
                size="small"
                icon="el-icon-position"
                class="md__ads-manage"
                @click="openAds"
              >{{ $t("machinesHub.manageAds") }}</el-button>
            </div>

            <div v-if="adsList.length" class="md__ads-list">
              <div v-for="ad in adsList" :key="ad.id" class="md__ad-card">
                <div class="md__ad-thumb">
                  <el-image v-if="ad.image" :src="ad.image" fit="cover">
                    <div slot="error" class="md__ad-thumb-fallback"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                  <div v-else class="md__ad-thumb-fallback"><i class="el-icon-picture-outline"></i></div>
                  <i v-if="ad.isVideo" class="el-icon-video-camera md__ad-video-badge"></i>
                </div>
                <div class="md__ad-info">
                  <div class="md__ad-title">{{ ad.title || $t("machinesHub.tabAds") }}</div>
                  <div class="md__ad-tags">
                    <span class="vtm-pill" :class="ad.isBanner ? 'vtm-pill--off' : 'vtm-pill--info'">
                      {{ ad.isBanner ? $t("machinesHub.adBanner") : $t("machinesHub.adScreen") }}
                    </span>
                    <span class="vtm-pill" :class="ad.isPaid ? 'vtm-pill--ok' : 'md__ad-tag--plain'">
                      {{ ad.isPaid ? $t("machinesHub.adPaid") : $t("machinesHub.adGallery") }}
                    </span>
                  </div>
                  <div v-if="ad.startTime || ad.endTime" class="md__ad-window">
                    {{ $t("machinesHub.adRunWindow") }}: <span class="vtm-num">{{ $formatDate(ad.startTime) }} – {{ $formatDate(ad.endTime) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="!adsLoading && adsLoaded" class="md__empty">{{ $t("machinesHub.adsNone") }}</div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>

<script>
  // 设备详情飞出：单机全部操作的唯一入口。
  // 概览就地编辑(自动保存) · 配置/支付内嵌(无二次弹窗) · 控制(远程) · 交易(含营收概要)。
  import { isDefaultMachineName } from "@/utils/machineDefaults";
  import { grantedLinks } from "@/utils/navSections";
  import { queryPlaces } from "@/utils/googlePlaces";
  import machineControlPanel from "_c/machine/user/MachineControlPanel";
  import configUserMachine from "_c/machine/user/ConfigUserMachine";
  import payment from "_c/machine/user/Payment";

  export default {
    name: "MachineDetailFlyout",
    components: { machineControlPanel, configUserMachine, payment },
    props: {
      visible: { type: Boolean, default: false },
      machine: { type: Object, default: null },
      configSubmit: { type: Object, default: () => ({}) },
      payment: { type: Object, default: () => ({}) },
      machineGroup: { type: Array, default: () => [] },
      yesNo: { type: Array, default: () => [] },
      deviceMode: { type: Array, default: () => [] },
      shuaKaQi: { type: Array, default: () => [] },
      coinAcceptorItem: { type: Array, default: () => [] },
      billAcceptorItem: { type: Array, default: () => [] },
      ageDevice: { type: Array, default: () => [] },
      feelLock: { type: Array, default: () => [] },
      ageType: { type: Array, default: () => [] },
      priorityMode: { type: Array, default: () => [] },
      idcardCountry: { type: Array, default: () => [] },
      isWarehouseAccount: { type: Boolean, default: false }
    },
    data() {
      return {
        tab: "overview",
        tabGuardOff: false, // 代码切页时跳过未保存确认
        form: { equipmentName: "", teamId: "", servicePhone: "", enableStatus: 1, locale: "", notes: "", lat: "", lng: "" },
        ready: false,
        baseline: "",
        saveState: "idle",
        saveTimer: null,
        revertTimer: null,
        txLoading: false,
        txList: [],
        adsLoading: false,
        adsLoaded: false,
        adsList: [],
        // 本机广告展示设置（迁移自配置页）
        adCfg: { isTAd: 0, isPAd: 0, isCountdown: 0, countdown: "" },
        adCfgSaving: false,
        // 账户级轮播时长（AD_PLAN，只读展示）
        acctRotation: null
      };
    },
    computed: {
      show: {
        get() {
          return this.visible;
        },
        set(v) {
          this.$emit("update:visible", v);
        }
      },
      // 机名仍为出厂默认（Slim Wall 等）→ 提示重命名
      nameIsDefault() {
        return isDefaultMachineName(this.form.equipmentName);
      },
      // 续费日期已过期 → 红色
      renewalExpired() {
        return !!(this.machine && this.machine.expireDate && new Date(this.machine.expireDate) <= new Date());
      },
      // 续费费用记录页是否已授权（导航已隐藏，深链仍可用）
      canRenewHistory() {
        return grantedLinks(this.$store.state.menuList).has("/order/renewal-record");
      },
      // Adverts 枢纽是否已授权（决定"Manage advertising"深链按钮是否展示）
      canManageAds() {
        return grantedLinks(this.$store.state.menuList).has("/adverts/adverts");
      },
      // 概览表单是否有未保存的改动
      isDirty() {
        return this.ready && JSON.stringify(this.form) !== this.baseline;
      },
      statusText() {
        if (this.saveState === "saving") {
          return this.$t("machinesHub.saving");
        }
        if (this.isDirty) {
          return this.$t("machinesHub.unsaved");
        }
        if (this.saveState === "saved") {
          return this.$t("machinesHub.saved");
        }
        return "";
      },
      canSubscribe() {
        // isTransfer !== 0 表示这台机器已经在下级账号名下：不是本账号的机器，
        // 就不提示订阅、也不因为"未订阅"而收走操作入口（与列表页的口径一致）
        return (
          this.machine &&
          Number(this.machine.isTransfer || 0) === 0 &&
          Number(this.machine.isTransferNew) === 1 &&
          !this.machine.stripeSubscriptionId &&
          !this.isWarehouseAccount
        );
      },
      revenueTotal() {
        return this.txList.reduce((s, t) => s + (Number(t.payablePrice) || 0), 0);
      },
      avgSale() {
        return this.txList.length ? this.revenueTotal / this.txList.length : 0;
      },
      // 账户级轮播时长一行摘要（屏广/顶部）
      acctRotationText() {
        const r = this.acctRotation;
        if (!r) {
          return "";
        }
        const parts = [];
        if (r.advertScreenOnce) {
          parts.push(this.$t("machinesHub.acctScreenRotation", { n: r.advertScreenOnce }));
        }
        if (r.operationTopOnce) {
          parts.push(this.$t("machinesHub.acctTopRotation", { n: r.operationTopOnce }));
        }
        return parts.join(" · ");
      }
    },
    watch: {
      tab(t) {
        if (t === "transactions" && !this.txList.length) {
          this.loadTransactions();
        }
        if (t === "ads" && !this.adsLoaded) {
          this.loadAds();
        }
        if (t === "ads") {
          this.seedAdCfg();
          this.loadAcctRotation();
        }
      }
    },
    methods: {
      onOpened() {
        // 代码切回概览（重新打开面板）不走切页确认：上次关闭时已确认过放弃
        this.tabGuardOff = true;
        this.tab = "overview";
        this.$nextTick(() => {
          this.tabGuardOff = false;
        });
        this.txList = [];
        this.adsLoaded = false;
        this.adsList = [];
        this.initForm();
        this.seedAdCfg();
      },
      // 读取账户级轮播时长（AD_PLAN，只读展示；编辑在广告枢纽）
      loadAcctRotation() {
        if (this.acctRotation) {
          return;
        }
        this.$api
          .request(this.$url.Promote.get, this.$method.get, { type: "AD_PLAN" })
          .then(res => {
            if (res.code === 0 && res.data) {
              this.acctRotation = res.data;
            }
          })
          .catch(() => {});
      },
      // 从机器配置读取广告展示开关到本地表单
      seedAdCfg() {
        const cfg = (this.configSubmit && this.configSubmit.config) || {};
        const info = cfg.info || {};
        const advert = cfg.advert || {};
        this.adCfg = {
          isTAd: Number(info.isTAd) === 1 ? 1 : 0,
          isPAd: Number(info.isPAd) === 1 ? 1 : 0,
          isCountdown: Number(advert.isCountdown) === 1 ? 1 : 0,
          countdown: advert.countdown != null ? advert.countdown : ""
        };
      },
      // 保存广告展示设置：并入完整 config 后 PUT userEquipment/config（与配置页同一契约）
      saveAdSettings() {
        if (!this.configSubmit || !this.configSubmit.config) {
          this.$message.warning(this.$t("common.noData"));
          return;
        }
        const payload = JSON.parse(JSON.stringify(this.configSubmit));
        delete payload.mqState; // 仅界面用，不回写
        payload.config.info = payload.config.info || {};
        payload.config.advert = payload.config.advert || {};
        payload.config.info.isTAd = this.adCfg.isTAd;
        payload.config.info.isPAd = this.adCfg.isPAd;
        payload.config.advert.isCountdown = this.adCfg.isCountdown;
        payload.config.advert.countdown = this.adCfg.isTAd === 1 && this.adCfg.isCountdown === 1 ? this.adCfg.countdown : payload.config.advert.countdown;
        this.adCfgSaving = true;
        this.$api
          .request(this.$url.UserMachine.modify, this.$method.put, payload)
          .then(res => {
            this.adCfgSaving = false;
            if (res.code === 0) {
              // 写回本地 config，保持飞出面板内状态一致
              this.configSubmit.config.info.isTAd = this.adCfg.isTAd;
              this.configSubmit.config.info.isPAd = this.adCfg.isPAd;
              this.configSubmit.config.advert.isCountdown = this.adCfg.isCountdown;
              this.configSubmit.config.advert.countdown = payload.config.advert.countdown;
              // 通知列表刷新：否则列表里那行还是旧配置，下次打开面板会用旧值重新填表，
              // 开关看起来"又变回去了"（与面板里其它保存一致，它们都会 emit saved）
              this.$emit("saved");
              this.$notify({ title: this.$t("common.tip"), message: this.$t("editUserMachine.up"), type: "success" });
            } else {
              this.$message.warning(res.msg);
            }
          })
          .catch(() => {
            this.adCfgSaving = false;
          });
      },
      // 打开续费费用记录页（原 Fee Tracker）
      goRenewHistory() {
        // 先走未保存确认，否则跳转会静默丢掉概览/配置的改动
        this.beforeClose(() => {
          this.show = false;
          if (this.$route.path !== "/order/renewal-record") {
            this.$router.push("/order/renewal-record");
          }
        });
      },
      // 跳到广告页同样会关闭面板，先确认未保存改动
      openAds() {
        this.beforeClose(() => this.$emit("open-ads", this.machine));
      },
      // 配置页签用 v-if 渲染，切走就会销毁它和未保存的改动：切走前先确认
      beforeTabLeave(to, from) {
        if (this.tabGuardOff || from !== "config" || !(this.$refs.cfg && this.$refs.cfg.dirty)) {
          return true;
        }
        return this.$confirm(this.$t("machinesHub.unsavedConfirm"), this.$t("machinesHub.unsavedTitle"), {
          confirmButtonText: this.$t("machinesHub.discard"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        });
      },
      initForm() {
        if (!this.machine) {
          return;
        }
        this.ready = false;
        const m = this.machine;
        this.form = {
          equipmentName: m.equipmentName || "",
          teamId: m.teamId || (m.userEquipmentTeam && m.userEquipmentTeam.teamId) || "",
          servicePhone: m.servicePhone || "",
          enableStatus: m.enableStatus !== undefined && m.enableStatus !== null ? Number(m.enableStatus) : 1,
          locale: m.locale || "",
          notes: m.notes || "",
          lat: m.lat != null ? m.lat : "",
          lng: m.lng != null ? m.lng : ""
        };
        this.baseline = JSON.stringify(this.form);
        this.saveState = "idle";
        this.$nextTick(() => {
          this.ready = true;
        });
      },
      // Google Places 地址联想（el-autocomplete fetch-suggestions）
      queryAddress(queryString, cb) {
        queryPlaces(queryString)
          .then(list => {
            // el-autocomplete 用 value 回填输入框；用 formatted_address 作为地址值
            cb((list || []).map(it => ({ ...it, value: it.address || it.name })));
          })
          .catch(() => cb([]));
      },
      // 选中联想项：写入地址 + 经纬度（随概览自动保存持久化）
      onAddressSelect(item) {
        if (!item) {
          return;
        }
        this.form.locale = item.address || item.name || this.form.locale;
        if (item.lat !== "" && item.lat != null) {
          this.form.lat = item.lat;
        }
        if (item.lng !== "" && item.lng != null) {
          this.form.lng = item.lng;
        }
      },
      discardChanges() {
        this.initForm();
      },
      // 关闭面板时若有未保存改动，先确认
      beforeClose(done) {
        const close = () => {
          // 关闭时就切回概览（已确认放弃），下次打开不会先闪出旧的配置页
          this.tabGuardOff = true;
          this.tab = "overview";
          this.$nextTick(() => {
            this.tabGuardOff = false;
          });
          done();
        };
        // 配置页签有自己的未保存标记（dirty），关闭时一并确认
        const cfgDirty = !!(this.tab === "config" && this.$refs.cfg && this.$refs.cfg.dirty);
        if (!this.isDirty && !cfgDirty) {
          close();
          return;
        }
        this.$confirm(this.$t("machinesHub.unsavedConfirm"), this.$t("machinesHub.unsavedTitle"), {
          confirmButtonText: this.$t("machinesHub.discard"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        })
          .then(() => {
            this.initForm();
            close();
          })
          .catch(() => {});
      },
      doSave() {
        // 只有空格的名称也算空（否则列表里显示成「-」）
        this.form.equipmentName = (this.form.equipmentName || "").trim();
        if (!this.form.equipmentName) {
          this.$message.warning(this.$t("machinesHub.nameRequired"));
          return;
        }
        this.saveState = "saving";
        const payload = {
          id: this.machine.id,
          equipmentName: this.form.equipmentName,
          teamId: this.form.teamId,
          servicePhone: this.form.servicePhone,
          enableStatus: this.form.enableStatus,
          locale: this.form.locale,
          notes: this.form.notes,
          lat: this.form.lat,
          lng: this.form.lng
        };
        this.$api
          .request(this.$url.UserMachine.update, this.$method.put, payload)
          .then(res => {
            if (res.code === 0) {
              this.baseline = JSON.stringify(this.form);
              this.saveState = "saved";
              this.$emit("saved");
              if (this.revertTimer) {
                clearTimeout(this.revertTimer);
              }
              this.revertTimer = setTimeout(() => {
                this.saveState = "idle";
              }, 2000);
            } else {
              this.saveState = "idle";
              this.$message.warning(res.msg);
            }
          })
          .catch(err => {
            this.saveState = "idle";
            this.$message.warning((err && err.msg) || "save failed");
          });
      },
      // 内嵌配置/支付保存或取消后：刷新行数据
      onSubEditDone(dialog, state) {
        if (state) {
          this.$emit("saved");
        }
      },
      loadTransactions() {
        this.txLoading = true;
        this.$api
          .request(this.$url.Order.page, this.$method.get, { equipmentNo: this.machine.equipmentNo, pageIndex: 1, pageSize: 15 })
          .then(res => {
            if (res.code === 0 && res.data) {
              this.txList = res.data.records || [];
            }
            this.txLoading = false;
          })
          .catch(() => {
            this.txLoading = false;
          });
      },
      // 本机当前投放广告一览（只读）：与 MachineAdvertGroup.vue 的解析逻辑保持一致
      parseBackendDateTime(str) {
        if (!str) {
          return null;
        }
        const s = String(str).trim();
        if (!s) {
          return null;
        }
        const normalized = s.replace(/-/g, "/");
        const d = new Date(normalized);
        return Number.isNaN(d.getTime()) ? null : d;
      },
      isExpiredAd(ad) {
        if (!ad) {
          return false;
        }
        const endRaw = ad.endTime != null ? ad.endTime : ad.end_time;
        const end = this.parseBackendDateTime(endRaw);
        if (!end) {
          return false;
        }
        return end.getTime() < Date.now();
      },
      mapAd(ad) {
        return {
          id: ad.id != null && ad.id !== "" ? ad.id : (ad.adId != null ? ad.adId : `ad-${Math.random()}`),
          title: ad.head || ad.title || "",
          brand: ad.advertiser || ad.brand || "",
          image: ad.url || ad.thum || ad.image || "",
          isBanner: ad.display === 2,
          isPaid: ad.adType === 1,
          isVideo: ad.type === 1,
          startTime: ad.startTime,
          endTime: ad.endTime
        };
      },
      loadAds() {
        if (!this.machine || !this.machine.equipmentNo) {
          return;
        }
        this.adsLoading = true;
        this.$api
          .request(this.$url.userEquipmentAd.page, this.$method.get, {
            equipmentNo: this.machine.equipmentNo,
            pageIndex: 1,
            pageSize: 1,
            isSearchCount: true
          })
          .then(res => {
            const ok = res.code === 0 || res.code === 200;
            let list = [];
            if (ok && res.data) {
              const records = res.data.records || res.data.list || (Array.isArray(res.data) ? res.data : []);
              const record = records[0];
              const rawAds = (record && record.ad) || [];
              list = rawAds.filter(a => !this.isExpiredAd(a)).map(a => this.mapAd(a));
            }
            this.adsList = list;
          })
          .catch(() => {
            this.adsList = [];
          })
          .finally(() => {
            this.adsLoaded = true;
            this.adsLoading = false;
          });
      }
    }
  };
</script>

<style scoped lang="scss">
  // Element UI's own drawer body has no definite height/overflow constraint by
  // default, so long tab content (e.g. Config) spilled past the viewport and
  // scrolled the whole page instead of just the drawer. Pin it here so our
  // internal flex/overflow chain (.md -> .md__tabs -> .md__embed) actually clips.
  ::v-deep .el-drawer__body {
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .md {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .md__top {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 20px 20px 16px;
    border-bottom: 1px solid var(--vtm-line);
  }
  /* 出厂默认值提示（机名/密码未修改） */
  .md__input--warn ::v-deep .el-input__inner {
    border-color: var(--vtm-warn);
    background: var(--vtm-warn-soft);
  }
  .md__field-warn {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.45;
    color: var(--vtm-warn);

    i {
      margin-right: 3px;
    }
  }
  .md__avatar {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-md);
    background: var(--vtm-navy);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
  }
  .md__head {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .md__renewal {
    font-size: 12px;
    color: var(--vtm-ink-3);

    .vtm-num {
      color: var(--vtm-ink);
      font-weight: 600;
    }
    .md__renewal--crit {
      color: var(--vtm-crit);
    }
  }
  .md__renewal-link {
    margin-left: 8px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--vtm-mint-ink);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
  .md__name {
    font-family: var(--vtm-display);
    font-size: 18px;
    font-weight: 600;
    color: var(--vtm-heading);
    word-break: break-word;
  }
  .md__no {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
  .md__pills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .md__close {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: var(--vtm-ink-3);
    font-size: 18px;
    cursor: pointer;

    &:hover {
      color: var(--vtm-crit);
    }
  }
  .md__tabs {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    ::v-deep .el-tabs__header {
      padding: 0 20px;
      margin-bottom: 0;
    }
    ::v-deep .el-tabs__content {
      flex: 1;
      overflow: hidden;
    }
    ::v-deep .el-tab-pane {
      height: 100%;
    }
  }
  .md__body {
    padding: 18px 20px 24px;
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
  }
  .md__embed {
    padding: 14px 20px 20px;
    height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
  }
  .md__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .md__field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 14px;
    min-width: 0;
  }
  .md__field--switch {
    align-items: flex-start;
  }
  .md__label {
    font-size: 12px;
    font-weight: 600;
    color: var(--vtm-ink-2);
  }
  .md__status {
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: flex-end;
    font-size: 12px;
    color: var(--vtm-ink-3);
    min-height: 18px;
    margin-bottom: 12px;
  }
  .md__status--saved {
    color: var(--vtm-ok);
  }
  .md__status--dirty {
    color: var(--vtm-warn);
  }
  .md__actions {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;

    .el-button {
      width: 100%;
      margin-left: 0;
    }
  }
  .md__subbanner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px;
    background: var(--vtm-warn-soft);
    border-bottom: 1px solid var(--vtm-line);
  }
  .md__subbanner-text {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-warn);

    i {
      margin-right: 4px;
    }
  }
  .md__more {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid var(--vtm-line);
  }
  .md__more-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--vtm-ink-3);
  }
  .md__more-links {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 6px;
  }
  .md__revenue {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 12px;
    margin-bottom: 16px;
    background: var(--vtm-well);
    border-radius: var(--vtm-r-md);
  }
  .md__rev-item {
    display: flex;
    flex-direction: column;
    text-align: center;
  }
  .md__rev-val {
    font-family: var(--vtm-display);
    font-size: 17px;
    font-weight: 600;
    color: var(--vtm-heading);
  }
  .md__rev-label {
    font-size: 10.5px;
    color: var(--vtm-ink-3);
    margin-top: 2px;
  }
  .md__empty {
    text-align: center;
    color: var(--vtm-ink-3);
    font-size: 13px;
    padding: 30px 0;
  }
  .md__txn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid var(--vtm-line);
  }
  .md__txn-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .md__txn-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
  }
  .md__txn-no {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
  }
  .md__txn-right {
    flex-shrink: 0;
    text-align: right;
  }
  .md__txn-amt {
    font-size: 14px;
    font-weight: 700;
    color: var(--vtm-heading);
  }
  .md__txn-time {
    display: block;
    font-size: 11px;
    color: var(--vtm-ink-3);
  }

  /* 广告页签 */
  /* 广告展示设置块 */
  .md__adcfg {
    margin-bottom: 16px;
    padding: 14px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-well);
  }
  .md__adcfg-title {
    font-family: var(--vtm-display);
    font-size: 13.5px;
    font-weight: 600;
    color: var(--vtm-heading);
    margin-bottom: 10px;
  }
  .md__adcfg-row {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-bottom: 10px;
  }
  .md__adcfg-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .md__adcfg-label {
    font-size: 12.5px;
    color: var(--vtm-ink-2);
  }
  .md__adcfg-actions {
    margin-top: 4px;
  }
  .md__adcfg-acct {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid var(--vtm-line);
    font-size: 12px;
    color: var(--vtm-ink-3);

    i {
      margin-right: 4px;
    }
  }

  .md__ads-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    min-height: 32px;
  }
  .md__ads-count {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink-2);
  }
  .md__ads-manage {
    margin-left: auto;
  }
  .md__ads-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .md__ad-card {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    background: var(--vtm-surface);
  }
  .md__ad-thumb {
    position: relative;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--vtm-well);

    ::v-deep .el-image {
      width: 100%;
      height: 100%;
    }
    ::v-deep .el-image img {
      object-fit: cover;
    }
  }
  .md__ad-thumb-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--vtm-ink-3);
    font-size: 18px;
  }
  .md__ad-video-badge {
    position: absolute;
    bottom: 2px;
    right: 2px;
    font-size: 11px;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    border-radius: 3px;
    padding: 1px 3px;
  }
  .md__ad-info {
    flex: 1;
    min-width: 0;
  }
  .md__ad-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .md__ad-tags {
    display: flex;
    gap: 6px;
    margin-top: 6px;
    flex-wrap: wrap;
  }
  .md__ad-tag--plain {
    background: transparent;
    color: var(--vtm-ink-2);
    border: 1px solid var(--vtm-line-2);
  }
  .md__ad-window {
    margin-top: 6px;
    font-size: 11.5px;
    color: var(--vtm-ink-3);
  }
</style>

<!-- 地址联想下拉挂到 body，需非 scoped 样式 -->
<style lang="scss">
  .md-address-suggest .md-addr-item {
    display: flex;
    flex-direction: column;
    line-height: 1.35;
    padding: 3px 0;
  }
  .md-address-suggest .md-addr-name {
    font-size: 13px;
    color: var(--vtm-heading);
    font-weight: 600;
  }
  .md-address-suggest .md-addr-line {
    font-size: 12px;
    color: var(--vtm-ink-3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
